import React, {useEffect, useRef, useState} from "react";
import Cropper from 'react-cropper';
import {ch} from "@renta-apps/athenaeum-react-common";
import {FileModel, Utility} from "@renta-apps/athenaeum-toolkit";
import type {ReactCropperElement} from 'react-cropper';
import type {ChangeEvent, DragEvent, RefObject} from 'react';

import "cropperjs/dist/cropper.css";
import "./CropperOverride.scss";

import styles from "./ToolsImageInput.module.scss";
import RentaToolsConstants from "@/helpers/RentaToolsConstants";
import Localizer from "@/localization/Localizer";
import {Button, ButtonType, Checkbox, NoSwipeNavigation} from "@renta-apps/athenaeum-react-components";
import {CropperHelpers} from "@/helpers/CropperHelpers";
import ToolsUtility from "@/helpers/ToolsUtility";
import UnleashHelper from "@/helpers/UnleashHelper";

enum ToolsImageInputView {

    /**
     * If no uploaded files, display nothing.
     * If single-input and uploaded picture, display small preview of the picture.
     * If multi-input and uploaded picture(s), display list of pictures.
     */
    Default,

    /**
     * Display full-screen preview of the selected picture.
     */
    Preview,

    /**
     * Display full-screen editor of the selected picture.
     */
    Edit,
}

export interface IToolsImageInputToolbar {

    /**
     * Should an "Upload file"-button be shown.
     */
    uploadButton?: boolean;

    /**
     * Should a "Take a picture"-button be shown.
     */
    takePictureButton?: boolean;

    /**
     * Should a "Remove"-button be shown.
     */
    deleteButton?: boolean;

    /**
     * Should a "Preview"-button be shown.
     */
    previewButton?: boolean;

    /**
     * If @{previewButton} is true, should the preview open in a new tab instead of an overlay?
     */
    previewInNewTab?: boolean;

    /**
     * Should a "Download"-button be shown.
     */
    downloadButton?: boolean;

    /**
     * Should an "Edit"-button be shown.
     */
    editButton?: boolean;

    /**
     * Should a "Rotate left"-button be shown.
     */
    rotateLeftButton?: boolean;

    /**
     * Should a "Rotate right"-button be shown.
     */
    rotateRightButton?: boolean;

    /**
     * Should a "Move up"-button be shown.
     */
    moveUpButton?: boolean;

    /**
     * Should a "Move down"-button be shown.
     */
    moveDownButton?: boolean;

    /**
     * Should a "Move to top"-button be shown.
     */
    moveToTopButton?: boolean;
}

interface IToolsImageInputProps {
    pictures: FileModel[] | string | null;
    className?: string;

    /**
     * Should Edit-mode be enabled immediately after an image is uploaded. Only works if {@link multi} is not set to true.
     */
    editOnAddInSingleMode?: boolean
    maxImageRequestSizeInBytes?: number;
    enableImageCheckbox?: boolean;
    minimizeOnEmpty?: boolean;

    /**
     * Does the {@link ImageInput} accept multiple images.
     * @default false
     */
    multi?: boolean;

    /**
     * Displayed when {@link pictures} is empty or when no image is selected.
     */
    noSelectionToolbar?: IToolsImageInputToolbar;

    /**
     * Displayed when an image has been selected.
     * @default {@link IIMageInputToolbar.rotateLeftButton} {@link IIMageInputToolbar.rotateRightButton} {@link IIMageInputToolbar.editButton} {@link IIMageInputToolbar.previewButton} {@link IIMageInputToolbar.uploadButton} {@link IIMageInputToolbar.takePictureButton} {@link IIMageInputToolbar.deleteButton}
     */
    selectionToolbar?: IToolsImageInputToolbar;

    /**
     * Displayed when an image is being previewed in full-screen.
     */
    previewToolbar?: IToolsImageInputToolbar;

    /**
     * Displayed when an image is being edited.
     */
    editToolbar?: IToolsImageInputToolbar;

    /**
     * List of allowed file extensions.
     */
    fileTypes?: string[];

    noEdit?: boolean;

    readonly?: boolean;

    noAutoSelect?: boolean;

    imageUrl?(file: FileModel): string;

    convertImage?(file: FileModel): Promise<FileModel | null>;

    onChange?(pictures: FileModel[]): Promise<void>;
}

const ToolsImageInput = (props: IToolsImageInputProps) => {

    const [currentView, setCurrentView] = useState<ToolsImageInputView>(ToolsImageInputView.Default);
    const [isDragOver, setIsDragOver] = useState(false);
    const [previousView, setPreviousView] = useState<ToolsImageInputView>(ToolsImageInputView.Default);
    const [selectedPictureIndex, setSelectedPictureIndex] = useState<number | null>(null);
    const [pictures, setPictures] = useState<FileModel[]>([]);

    const fileInputRef = useRef<HTMLInputElement>(null);
    const cameraFileInputRef = useRef<HTMLInputElement>(null);
    const cropperRef = useRef<ReactCropperElement>(null);
    const cropperHelper = new CropperHelpers(cropperRef);

    // Utility functions
    const editOnAddInSingleMode = props.editOnAddInSingleMode === true;
    const hasSelectedPictureIndex = typeof selectedPictureIndex === "number";
    const isFullscreen = currentView === ToolsImageInputView.Preview || currentView === ToolsImageInputView.Edit;
    const multi = props.multi === true;
    const readonly = props.readonly === true;
    const minimizeOnEmpty = props.minimizeOnEmpty === true;
    const activePicture = hasSelectedPictureIndex ? pictures[selectedPictureIndex!] : null;

    useEffect(() => {
        initializePictures();
    }, [props.pictures])


    const initializePictures = () => {
        let _pictures: FileModel[] = [];
        let _selectedPictureIndex: number | null = null;

        if ((Array.isArray(props.pictures)) && (props.pictures.length > 0)) {
            _pictures = props.pictures;
            _selectedPictureIndex = selectedPictureIndex ?? 0;
        } else if (typeof props.pictures === "string") {
            _pictures = [new FileModel(props.pictures as string)];
            _selectedPictureIndex = 0;
        }

        if (props.noAutoSelect) {
            _selectedPictureIndex = null;
        }

        setPictures([..._pictures]);
        setSelectedPictureIndex(_selectedPictureIndex);
    }

    const cropperSource = (): string => {
        if ((!hasSelectedPictureIndex) || (!activePicture)) {
            return "";
        }

        if (activePicture.id) {
            if (props.imageUrl) {
                return props.imageUrl(activePicture);
            }

            return getImageUrl(activePicture);
        }

        return activePicture.src;
    }

    const getImageUrlFromImage = (image: FileModel): string => {
        return (image.src)
                ? image.src
                : `/files/images/${image.id}`;
    }

    const getImageUrl = (image: FileModel): string => {
        return (props.imageUrl)
                ? props.imageUrl(image)
                : getImageUrlFromImage(image);
    }

    const getPreviewName = (index: number): string => {
        const picture: FileModel | undefined = pictures[index];

        if (!picture) {
            return "";
        }

        return picture.name
    }

    const isImageMissing = (index: number): boolean => {
        if (!UnleashHelper.isEnabled(RentaToolsConstants.featureFlagDisplayImageError)){
            return false;
        }

        const picture: FileModel | undefined = pictures[index];

        return !picture || picture.src === RentaToolsConstants.notFound || ToolsUtility.isNullOrEmpty(picture.src) || (ToolsUtility.isNullOrEmpty(picture.id) && !picture.isUploading);
    }

    const markImageMissing = (index: number): void => {
        if (!UnleashHelper.isEnabled(RentaToolsConstants.featureFlagDisplayImageError)){
            return;
        }

        const picture: FileModel | undefined = pictures[index];

        if (picture) {
            picture.src = RentaToolsConstants.notFound;

            setPictures([...pictures]);
        }
    }

    const getPreviewSource = (index: number): string => {
        const picture: FileModel | undefined = pictures[index];

        if (!picture) {
            return "";
        }

        if (picture.id) {
            if (props.imageUrl) {
                const url = props.imageUrl(picture);
                return url;
            }

            return getImageUrl(picture);
        }

        return picture.src;
    }

    const maxImageRequestSizeInBytes = (): number => {
        return (props.maxImageRequestSizeInBytes) || (RentaToolsConstants.originalImageMaxSize);
    }

    const acceptedTypes = (): string => {
        return (props.fileTypes && props.fileTypes.length)
                ? props.fileTypes.join(",")
                : "image/*";
    }

    const canEdit = (): boolean => {
        return !(props.noEdit === true);
    }

    //  ViewIfStatements

    const toolbar = (): IToolsImageInputToolbar => {
        switch (currentView) {
            case ToolsImageInputView.Default:
                return (hasSelectedPictureIndex)
                        ? props.selectionToolbar ?? defaultSelectionToolbar()
                        : props.noSelectionToolbar ?? defaultNoSelectionToolbar();
            case ToolsImageInputView.Preview:
                return props.previewToolbar ?? defaultPreviewToolbar();
            case ToolsImageInputView.Edit:
                return props.editToolbar ?? defaultEditToolbar();
            default:
                throw new TypeError(`Non-existing enum value '${currentView}'`);
        }
    }

    const showBackButton = (): boolean => {
        return (isFullscreen);
    }

    const showSaveButton = (): boolean => {
        return (currentView === ToolsImageInputView.Edit);
    }

    const miniRotateButtons = (): boolean => {
        return (hasSelectedPictureIndex) && (currentView === ToolsImageInputView.Default);
    }

    //  Control panel button Click Events

    const onBrowseButtonClickAsync = async (): Promise<void> => {
        if (!fileInputRef) {
            return;
        }

        const ref: RefObject<HTMLInputElement> = fileInputRef as RefObject<HTMLInputElement>;

        if (!ref.current) {
            return
        }

        ref.current.click();
    }

    const onCameraButtonClick = async (): Promise<void> => {
        if (!cameraFileInputRef) {
            return;
        }

        const ref: RefObject<HTMLInputElement> = cameraFileInputRef as RefObject<HTMLInputElement>;

        if (!ref.current) {
            return
        }

        ref.current.click();
    }

    const onSaveButtonClickAsync = async (): Promise<void> => {
        if ((!cropperRef.current)
                || (currentView !== ToolsImageInputView.Edit)) {
            return;
        }

        if ((!hasSelectedPictureIndex) || (!activePicture)) {
            return;
        }

        const currentPicture = activePicture;

        const newFileModel: FileModel | null = {...currentPicture};

        newFileModel.src = cropperRef.current?.cropper.getCroppedCanvas().toDataURL() || "";

        await convertImage(newFileModel, currentPicture);

        await updatePictureAsync(newFileModel, selectedPictureIndex!);

        await setCurrentViewAsync(previousView);
    }

    const onEditButtonClickAsync = async (): Promise<void> => {
        if (!hasSelectedPictureIndex) {
            return;
        }

        await setCurrentViewAsync(ToolsImageInputView.Edit);
    }

    const onBackButtonClickAsync = async (): Promise<void> => {
        const newView: ToolsImageInputView = (currentView === ToolsImageInputView.Edit)
                ? previousView
                : ToolsImageInputView.Default;

        await setCurrentViewAsync(newView);
    }

    const onPreviewButtonClickAsync = async (): Promise<void> => {
        if (!hasSelectedPictureIndex) {
            return;
        }

        await setCurrentViewAsync(ToolsImageInputView.Preview);
    }

    const onDeleteButtonClickAsync = async (): Promise<void> => {
        if (!hasSelectedPictureIndex) {
            return;
        }

        await removePictureAsync(selectedPictureIndex!);
    }

    const onRotateButtonClickAsync = async (degrees: number): Promise<void> => {
        if (!cropperRef.current) {
            return;
        }

        cropperHelper.rotateAndFitToScreen(degrees);
    }

    const onRotateMiniButtonClickAsync = async (degrees: number): Promise<void> => {
        const selectedPicture = pictures[selectedPictureIndex!];

        const currentIndex = selectedPictureIndex!;

        const rotated: FileModel | null = await CropperHelpers.rotate(selectedPicture, degrees, getPreviewSource(selectedPictureIndex!));

        await convertImage(rotated, selectedPicture);

        await updatePictureAsync(rotated, currentIndex);
    }

    const onMoveToTopButtonClickAsync = async (): Promise<void> => {
        if ((!hasSelectedPictureIndex) || (selectedPictureIndex! <= 0)) {
            return;
        }

        await moveSelectedImageToTopAsync()
    }

    const onMoveUpButtonClickAsync = async (): Promise<void> => {
        if ((!hasSelectedPictureIndex) || (selectedPictureIndex! <= 0)) {
            return;
        }

        await moveSelectedImageUpDownAsync(true);
    }

    const onMoveDownButtonClickAsync = async (): Promise<void> => {
        if ((!hasSelectedPictureIndex) || (selectedPictureIndex! >= pictures.length)) {
            return;
        }

        await moveSelectedImageUpDownAsync(false);
    }

    const onListViewItemClick = (index: number, fileModel: FileModel): void => {
        if (!fileModel.isUploading) {
            const selectedIndex = (hasSelectedPictureIndex) && (selectedPictureIndex === index) ? null : index;
            setSelectedPictureIndex(selectedIndex)
        }
    }

    const onImageInputDragEnterAsync = async (event: DragEvent<HTMLDivElement>): Promise<void> => {
        event.preventDefault();

        if (!isDragOver) {
            setIsDragOver(true);
        }
    }

    const onDropDownAreaDragEnterAsync = async (event: DragEvent<HTMLDivElement>): Promise<void> => {
        event.preventDefault();

        if (!isDragOver) {
            setIsDragOver(true);
        }
    }

    const onDropDownAreaDragOverAsync = async (event: DragEvent<HTMLDivElement>): Promise<void> => {
        event.preventDefault();
    }

    const onDropDownAreaDragLeaveAsync = async (event: DragEvent<HTMLDivElement>): Promise<void> => {
        event.preventDefault();

        if (isDragOver) {
            setIsDragOver(false);
        }
    }

    const onDropDownAreaDropAsync = async (event: DragEvent<HTMLDivElement>): Promise<void> => {
        event.preventDefault();
        event.persist();

        if (isDragOver) {
            setIsDragOver(false);
        }

        if (!event?.dataTransfer?.files) {
            return;
        }

        await addFileListAsync(event.dataTransfer.files)
    }

    const onFileInputChangeAsync = async (event: ChangeEvent<HTMLInputElement>): Promise<void> => {
        event.preventDefault();

        if (!event.target.files) {
            return;
        }

        await addFileListAsync(event.target.files)
    }

    const setCurrentViewAsync = async (view: ToolsImageInputView): Promise<void> => {
        if (currentView !== assertIsImageInputView(view)) {
            setPreviousView(currentView);
            setCurrentView(view);
        }
    }

    const handleUncompletedUpload = (fileModel: FileModel) => {
        const filesState = pictures;

        filesState.remove(fileModel);

        setPictures([...filesState])
    }

    const handleCompletedUpload = (originalFile: FileModel, converted: FileModel | null) => {
        const filesState = pictures;
        const index = filesState.indexOf(originalFile);

        if (index > -1) {
            let pictureToReplace = converted ?? originalFile;
            pictureToReplace.isUploading = false;

            filesState[index] = pictureToReplace;

            setPictures([...filesState])
        }
    }

    const addFileListAsync = async (fileList: FileList): Promise<void> => {
        let fileListAsArray: File[] = Array.from(fileList);

        const ref: RefObject<HTMLInputElement> = fileInputRef as RefObject<HTMLInputElement>;
        if (ref.current) {
            ref.current.value = "";
        }

        if (fileListAsArray.length === 0) {
            return;
        }

        if ((!multi) && (fileListAsArray.length > 1)) {
            fileListAsArray = [fileListAsArray[0]];
        }

        let fileModels: FileModel[] = await Promise.all(fileListAsArray.map(async (file: File): Promise<FileModel> => {
            return await fileToFileModel(file);
        }));

        fileModels = fileModels.filter(fileModel => {
            if (fileModel.size < maxImageRequestSizeInBytes()) {
                return true;
            }

            ch.alertErrorAsync(Localizer.genericDocumentTooBig, true);
            return false;
        });

        await Promise.all(fileModels.map(async (fileModel): Promise<void> => {
            await convertImage(fileModel);
        }));

        if (pictures.length > 0) {
            await addPicturesAsync();
        }
    }

    const convertImage = async (fileModel: FileModel, originalFile: FileModel | null = null): Promise<void> => {
        const filesState = pictures;

        const index = originalFile ? filesState.indexOf(originalFile) : -1;

        fileModel.isUploading = true;

        if (index > -1) {
            filesState[index] = fileModel;
        } else if (multi) {
            filesState.push(fileModel);
        } else {
            filesState.splice(0, 1, fileModel);
        }

        setPictures([...filesState])

        let converted: FileModel | null = null;

        if (props.convertImage) {

            converted = await props.convertImage(fileModel);
            if (converted) {
                converted.isUploading = false;
                fileModel.isUploading = false;
            }

            if (converted === null) {
                handleUncompletedUpload(fileModel);
                return;
            }
        }

        handleCompletedUpload(fileModel, converted);
    }

    const addPicturesAsync = async (): Promise<void> => {
        if (readonly) {
            throw new Error(`Cannot add pictures to readonly ImageInput`);
        }

        if (props.onChange) {
            if (multi) {
                setSelectedPictureIndex(selectedPictureIndex ?? 0)
                await setCurrentViewAsync(ToolsImageInputView.Default)

                await props.onChange(pictures);
            } else {
                setSelectedPictureIndex(0)

                const selectedView = (editOnAddInSingleMode)
                        ? ToolsImageInputView.Edit
                        : ToolsImageInputView.Default

                await setCurrentViewAsync(selectedView);

                await props.onChange(pictures.slice(0, 1));
            }
        }
    }

    const updatePictureAsync = async (fileModel: FileModel, index: number): Promise<void> => {
        if (readonly) {
            throw new Error(`Cannot update pictures of readonly ImageInput`);
        }

        const _pictures = pictures.map((picture: FileModel, i) => {
            return index === i ? fileModel : picture;
        });

        if (props.onChange) {
            await props.onChange(_pictures);
        }
    }

    const changeImageEnabledAsync = async (enabled: boolean, index: number): Promise<void> => {
        if (readonly) {
            throw new Error(`Cannot change enabled state of pictures in readonly ImageInput`);
        }

        if (index < 0 || index >= pictures.length) {
            throw new Error(`Invalid index: ${index}`);
        }

        const _pictures = pictures;
        // Update the enabled property
        _pictures[index].enabled = enabled;

        setPictures([..._pictures]);

        if (props.onChange) {
            await props.onChange(_pictures);
        }
    }

    const moveSelectedImageToTopAsync = async (): Promise<void> => {
        if (readonly) {
            throw new Error(`Cannot move pictures of readonly ImageInput`);
        }

        if ((!hasSelectedPictureIndex) || (selectedPictureIndex === 0)) {
            return;
        }

        const oldImage: FileModel = pictures[selectedPictureIndex!];
        const imagesAfter: FileModel[] = pictures.slice();
        imagesAfter.remove(oldImage);

        setPictures([oldImage, ...imagesAfter])
        setSelectedPictureIndex(0);

        if (props.onChange) {
            await props.onChange(pictures);
        }
    }

    const moveSelectedImageUpDownAsync = async (up: boolean): Promise<void> => {
        if (readonly) {
            throw new Error(`Cannot move pictures of readonly ImageInput`);
        }

        if ((!hasSelectedPictureIndex) || ((up) && (selectedPictureIndex === 0)) || ((!up) && (selectedPictureIndex === pictures.length - 1))) {
            return;
        }

        const oldIndex: number = selectedPictureIndex!;
        const oldImage: FileModel = pictures[oldIndex];

        const newIndex: number = (up)
                ? oldIndex - 1
                : oldIndex + 1;

        pictures[oldIndex] = pictures[newIndex];
        pictures[newIndex] = oldImage;

        if (props.onChange) {
            await props.onChange(pictures);
        }

        setSelectedPictureIndex(newIndex)
    }

    const removePictureAsync = async (index: number): Promise<void> => {
        if (readonly) {
            throw new Error(`Cannot remove pictures of readonly ImageInput`);
        }

        const _pictures: FileModel[] = pictures;

        _pictures.splice(index, 1);

        let newIndex: number | null = (_pictures.length <= 0)
                ? null
                : (index <= 0)
                        ? 0
                        : index - 1;

        if (newIndex) {
            newIndex = _pictures[newIndex].isUploading ? null : newIndex;
        }

        setSelectedPictureIndex(newIndex);

        await setCurrentViewAsync(ToolsImageInputView.Default);

        if (props.onChange) {
            await props.onChange(_pictures);
        }
    }

    const defaultNoSelectionToolbar = (): IToolsImageInputToolbar => {
        return {
            takePictureButton: true,
            uploadButton: true,
        };
    }

    const defaultSelectionToolbar = (): IToolsImageInputToolbar => {
        return {
            deleteButton: true,
            editButton: true,
            previewButton: true,
            rotateLeftButton: true,
            rotateRightButton: true,
            takePictureButton: true,
            uploadButton: true,
        }
    }

    const defaultPreviewToolbar = (): IToolsImageInputToolbar => {
        return {
            deleteButton: true,
            editButton: true,
            uploadButton: true,
            takePictureButton: true,
        };
    }

    const defaultEditToolbar = (): IToolsImageInputToolbar => {
        return {
            rotateLeftButton: true,
            rotateRightButton: true,
            deleteButton: true,
        };
    }

    const fileToFileModel = async (file: File): Promise<FileModel> => {
        const fileData = await readFile(file);
        const fileModel = new FileModel(fileData);
        fileModel.type = file.type;
        fileModel.name = file.name;
        fileModel.lastModified = new Date(file.lastModified);
        fileModel.size = file.size;
        return fileModel;
    }

    const readFile = (file: File): Promise<string | null> => {
        return new Promise((resolve) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);

            reader.onload = function (e) {
                if (e.target?.result) {
                    resolve(e.target?.result as string);
                }
            };

            reader.onerror = function () {
                resolve(null);
            };
        });
    }

    const assertIsImageInputView = (value: any): ToolsImageInputView => {
        switch (value) {
            case ToolsImageInputView.Default:
                return ToolsImageInputView.Default;
            case ToolsImageInputView.Preview:
                return ToolsImageInputView.Preview;
            case ToolsImageInputView.Edit:
                return ToolsImageInputView.Edit;
            default:
                throw new TypeError("value is not of type ImageInputView");
        }
    }

    //  Renders

    const renderControlPanel = (): JSX.Element => {
        return (
            <React.Fragment>
                {
                    (!readonly) && canEdit() && ((toolbar().rotateLeftButton) || (toolbar().rotateRightButton)) &&
                    (
                        (miniRotateButtons())
                        ?
                        (
                            <div className={styles.controlPanelMiniButtonWrap}>

                                {
                                    (toolbar().rotateLeftButton) &&
                                    (
                                        <Button small
                                                icon={{name: "undo"}}
                                                type={ButtonType.Info}
                                                onClick={async () => await onRotateMiniButtonClickAsync(-90)}
                                        />
                                    )
                                }

                                {
                                    (toolbar().rotateRightButton) &&
                                    (
                                        <Button small
                                                icon={{name: "redo"}}
                                                type={ButtonType.Info}
                                                onClick={async () => await onRotateMiniButtonClickAsync(90)}
                                        />
                                    )
                                }

                            </div>
                        )
                        :
                        (
                            <>
                                {
                                    (toolbar().rotateLeftButton) &&
                                    (
                                        <Button small
                                                className={styles.controlPanelButton}
                                                icon={{name: "undo"}}
                                                type={ButtonType.Light}
                                                label={Localizer.genericRotateLeft}
                                                onClick={async () => await onRotateButtonClickAsync(-90)}
                                        />
                                    )
                                }

                                {
                                    (toolbar().rotateRightButton) &&
                                    (
                                        <Button small
                                                className={styles.controlPanelButton}
                                                icon={{name: "redo"}}
                                                type={ButtonType.Light}
                                                label={Localizer.genericRotateRight}
                                                onClick={async () => await onRotateButtonClickAsync(90)}
                                        />
                                    )
                                }
                            </>
                        )
                    )
                }

                {
                    (!readonly) && (toolbar().moveToTopButton) &&
                    (
                        <Button small
                                className={styles.controlPanelButton}
                                icon={{name: "level-up"}}
                                type={ButtonType.Info}
                                label={Localizer.genericMoveToTop}
                                onClick={async () => await onMoveToTopButtonClickAsync()}
                        />
                    )
                }

                {
                    (!readonly) && (toolbar().moveUpButton) &&
                    (
                        <Button small
                                className={styles.controlPanelButton}
                                icon={{name: "arrow-up"}}
                                type={ButtonType.Info}
                                label={Localizer.genericMoveUp}
                                onClick={async () => await onMoveUpButtonClickAsync()}
                        />
                    )
                }

                {
                    (!readonly) && (toolbar().moveDownButton) &&
                    (
                        <Button small
                                className={styles.controlPanelButton}
                                icon={{name: "arrow-down"}}
                                type={ButtonType.Info}
                                label={Localizer.genericMoveDown}
                                onClick={async () => await onMoveDownButtonClickAsync()}
                        />
                    )
                }

                {
                    (!readonly) && canEdit() && (toolbar().editButton) &&
                    (
                        <Button small
                                className={styles.controlPanelButton}
                                icon={{name: "crop"}}
                                type={ButtonType.Info}
                                label={Localizer.genericEdit}
                                onClick={async () => await onEditButtonClickAsync()}
                        />
                    )
                }

                {
                    (toolbar().previewButton) &&
                    (
                        (!!activePicture && toolbar().previewInNewTab)
                        ?
                        (
                            <a href={activePicture.src} target="_blank" rel="noopener noreferrer">
                                <Button small
                                        className={styles.controlPanelButton}
                                        icon={{name: "eye"}}
                                        type={ButtonType.Info}
                                        label={Localizer.genericPreview}
                                />
                            </a>
                        )
                        :
                        (
                            <Button small
                                    className={styles.controlPanelButton}
                                    icon={{name: "eye"}}
                                    type={ButtonType.Info}
                                    label={Localizer.genericPreview}
                                    onClick={async () => await onPreviewButtonClickAsync()}
                            />
                        )
                    )
                }

                {
                    (!!activePicture) && (toolbar().downloadButton) &&
                    (
                        <a href={activePicture.src} download>
                            <Button small
                                    className={styles.controlPanelButton}
                                    icon={{name: "download"}}
                                    type={ButtonType.Info}
                                    label={Localizer.genericDownload}
                            />
                        </a>
                    )
                }

                {
                    (!readonly) && (toolbar().uploadButton) &&
                    (
                        <Button small right
                                className={styles.controlPanelButton}
                                icon={{name: "file-import"}}
                                type={ButtonType.Orange}
                                label={Localizer.genericBrowse}
                                onClick={async () => await onBrowseButtonClickAsync()}
                        />
                    )

                }

                {
                    (!readonly) && (toolbar().takePictureButton) &&
                    (
                        <Button small right
                                className={styles.controlPanelButton}
                                icon={{name: "camera"}}
                                type={ButtonType.Orange}
                                label={Localizer.genericCamera}
                                onClick={async () => await onCameraButtonClick()}
                        />
                    )
                }

                {
                    (!readonly) && (showSaveButton()) &&
                    (
                        <Button small right
                                className={styles.controlPanelButton}
                                icon={{name: "save"}}
                                type={ButtonType.Success}
                                label={Localizer.genericSave}
                                onClick={async () => await onSaveButtonClickAsync()}
                        />
                    )
                }

                {
                    (showBackButton()) &&
                    (
                        <Button small
                                className={styles.controlPanelButton}
                                icon={{name: "arrow-left"}}
                                type={ButtonType.Info}
                                label={Localizer.genericBack}
                                onClick={async () => await onBackButtonClickAsync()}
                        />
                    )
                }

                {
                    (!readonly) && (toolbar().deleteButton) &&
                    (
                        <Button small
                                className={styles.controlPanelButton}
                                icon={{name: "trash"}}
                                type={ButtonType.Warning}
                                label={Localizer.genericDelete}
                                onClick={async () => await onDeleteButtonClickAsync()}
                        />
                    )
                }
            </React.Fragment>
        );
    }

    const renderListViewItem = (fileModel: FileModel, index: number): JSX.Element => {
        const activeListViewItemStyle: string | false = (hasSelectedPictureIndex) && (selectedPictureIndex === index) && styles.activeListViewItem;
        const key: string = `${index}_${fileModel.id}_${fileModel.name}`;

        return (
            <div className={styles.listViewContainer} key={key}>
                {
                    (props.enableImageCheckbox && index !== 0) && (
                        <Checkbox value={fileModel.enabled}
                                  onChange={async (_, enabled) => await changeImageEnabledAsync(enabled, index)}
                        />
                    )
                }

                <div className={Utility.css(styles.listViewItem, activeListViewItemStyle)}
                     onClick={() => onListViewItemClick(index, fileModel)}
                >


                    <div className={styles.listViewItemThumbnail}>
                        <img src={getPreviewSource(index)}
                             alt={getPreviewName(index)}
                             onError={() => markImageMissing(index)}
                        />
                    </div>

                    {
                        getPreviewName(index)
                    }

                    {
                        (isImageMissing(index)) && (
                            <span className={styles.danger}> {props.readonly ? Localizer.genericFileNotFound : Localizer.rentaToolsControllerErrorUploadingFullSizedImageFailed} </span>
                        )
                    }


                    {fileModel.isUploading && (
                        <div className={styles.uploadingProgress}>
                            <div className={styles.ldsEllipsis}>
                                <div/>
                                <div/>
                                <div/>
                                <div/>
                            </div>
                        </div>
                    )}

                </div>
            </div>
        );
    }

    const renderListView = (): JSX.Element => {
        return (
            <div className={styles.listView}>
                {
                    pictures.map((picture, index) => renderListViewItem(picture, index))
                }
            </div>
        );
    }

    const renderPreviewPanel = (): JSX.Element => {
        const index: number = selectedPictureIndex ?? 0;
        const src: string | undefined = getPreviewSource(index);
        const alt: string | undefined = getPreviewName(index)

        return (
            <div className={styles.preview}>
                <img src={src}
                     alt={alt}
                />
            </div>
        );
    }

    const renderCropperPanel = (): JSX.Element => {
        return (
            <div className={styles.cropper}>
                <Cropper ref={cropperRef}
                         className={styles.reactCropper}
                         style={{height: "100%", width: "100%"}}
                         src={cropperSource()}
                         viewMode={1} // cannot move box outside image borders
                         guides={false}
                         ready={() => cropperHelper.setCropAreaToImageFullSize()}
                />
            </div>
        )
    }

    const minimizeStyle: string | null = (minimizeOnEmpty) && (pictures.length <= 0)
            ? styles.minimize
            : null;
    const fullScreenStyle: string | null = (isFullscreen)
            ? styles.fullScreen
            : null;

    return (
        <div className={Utility.css(styles.ToolsImageInput, minimizeStyle, fullScreenStyle, props.className)}
             onDragEnter={(event: DragEvent<HTMLDivElement>) => onImageInputDragEnterAsync(event)}>

            <input ref={fileInputRef}
                   className={styles.fileInput}
                   type="file"
                   accept={acceptedTypes()}
                   multiple={multi}
                   onChange={(event: ChangeEvent<HTMLInputElement>) => onFileInputChangeAsync(event)}
            />

            <input ref={cameraFileInputRef}
                   className={styles.fileInput}
                   type="file"
                   accept={acceptedTypes()}
                   capture="environment"
                   multiple={multi}
                   onChange={(event: ChangeEvent<HTMLInputElement>) => onFileInputChangeAsync(event)}
            />

            <div className={styles.controlPanel}>
                {
                    renderControlPanel()
                }
            </div>

            <NoSwipeNavigation className={styles.viewPanel}>

                <div className={Utility.css(styles.dragDropArea, (isDragOver) && styles.dragDropAreaActive)}
                     onDrop={(event: DragEvent<HTMLDivElement>) => onDropDownAreaDropAsync(event)}
                     onDragOver={(event: DragEvent<HTMLDivElement>) => onDropDownAreaDragOverAsync(event)}
                     onDragEnter={(event: DragEvent<HTMLDivElement>) => onDropDownAreaDragEnterAsync(event)}
                     onDragLeave={(event: DragEvent<HTMLDivElement>) => onDropDownAreaDragLeaveAsync(event)}
                >
                    <span className={styles.dragDropAreaOverlay}>
                        {Localizer.genericDropIt}
                    </span>
                </div>

                {
                    (currentView === ToolsImageInputView.Edit) &&
                    (
                        renderCropperPanel()
                    )
                }

                {
                    (multi) && (currentView === ToolsImageInputView.Default) &&
                    (
                        renderListView()
                    )
                }

                {
                    ((currentView === ToolsImageInputView.Preview) || ((!multi) && (currentView === ToolsImageInputView.Default) && (hasSelectedPictureIndex))) &&
                    (
                        renderPreviewPanel()
                    )
                }

            </NoSwipeNavigation>

        </div>
    );
}

export default ToolsImageInput;